import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearCuratorSession,
  curatorLogin as apiLogin,
  curatorLogout as apiLogout,
  curatorSession,
  getStoredCuratorToken,
  isStoredSessionExpired,
  setCuratorLanding as apiSetLanding,
} from '../lib/curator';

const CuratorContext = createContext(null);

export function CuratorProvider({ children }) {
  const [isCurator, setIsCurator] = useState(() => {
    const token = getStoredCuratorToken();
    return Boolean(token && !isStoredSessionExpired());
  });
  const [expiresAt, setExpiresAt] = useState(null);
  const [checking, setChecking] = useState(true);

  const refreshSession = useCallback(async () => {
    setChecking(true);
    try {
      const session = await curatorSession();
      setIsCurator(Boolean(session?.curator));
      setExpiresAt(session?.expires_at || null);
      if (!session?.curator) clearCuratorSession();
    } catch {
      setIsCurator(false);
      setExpiresAt(null);
      clearCuratorSession();
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  // Client-side TTL check while tab is open
  useEffect(() => {
    if (!isCurator) return undefined;
    const id = window.setInterval(() => {
      if (isStoredSessionExpired()) {
        clearCuratorSession();
        setIsCurator(false);
        setExpiresAt(null);
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [isCurator]);

  const login = useCallback(async (password) => {
    const data = await apiLogin(password);
    setIsCurator(true);
    setExpiresAt(data?.expires_at || null);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setIsCurator(false);
    setExpiresAt(null);
  }, []);

  const setLanding = useCallback(async ({ projectId, analysisId, chartView }) => {
    return apiSetLanding({ projectId, analysisId, chartView });
  }, []);

  const value = useMemo(
    () => ({
      isCurator,
      expiresAt,
      checking,
      login,
      logout,
      setLanding,
      refreshSession,
    }),
    [isCurator, expiresAt, checking, login, logout, setLanding, refreshSession],
  );

  return <CuratorContext.Provider value={value}>{children}</CuratorContext.Provider>;
}

export const useCurator = () => {
  const ctx = useContext(CuratorContext);
  if (!ctx) throw new Error('useCurator must be within CuratorProvider');
  return ctx;
};
